/**
 * QuickContextStep — company name + role / teamSize / industry radio cards.
 * Drives demo-data personalisation on the next step.
 *
 * The company name becomes `orgs.name`. It is asked here because nothing else
 * in the SaaS flow ever does: without it the backend falls back to the generic
 * "My Workspace", which then shows up in the app header, in invitations and in
 * every transactional email the org receives.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup } from '@/components/ui/radio';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { ArrowRight } from 'lucide-react';
import type { OnboardingRole, TeamSize, Industry } from '@aica/api-types/schemas';

const ROLES: OnboardingRole[] = ['owner', 'sales_manager', 'agent', 'hr', 'other'];
const TEAM_SIZES: TeamSize[] = ['1-10', '11-50', '51+'];
const INDUSTRIES: Industry[] = [
  'saas',
  'real_estate',
  'call_center',
  'ecommerce',
  'financial_services',
  'healthcare',
  'other',
];

export interface QuickContextValues {
  organizationName: string;
  role: OnboardingRole;
  teamSize: TeamSize;
  industry: Industry;
  otherIndustry?: string;
}

/** Mirrors `saasOnboardingRequestSchema.organizationName` (min 2, max 100). */
const ORG_NAME_MIN = 2;
const ORG_NAME_MAX = 100;

interface QuickContextStepProps {
  onNext: (values: QuickContextValues) => void;
  isSubmitting?: boolean;
  error?: string | null;
}

export function QuickContextStep({
  onNext,
  isSubmitting = false,
  error,
}: QuickContextStepProps) {
  const { t } = useTranslation(['onboarding']);
  const [organizationName, setOrganizationName] = useState('');
  const [role, setRole] = useState<OnboardingRole | null>(null);
  const [teamSize, setTeamSize] = useState<TeamSize | null>(null);
  const [industry, setIndustry] = useState<Industry | null>(null);
  const [otherIndustry, setOtherIndustry] = useState('');

  const trimmedOrgName = organizationName.trim();
  const needsOther = industry === 'other';
  const isValid =
    trimmedOrgName.length >= ORG_NAME_MIN &&
    role !== null &&
    teamSize !== null &&
    industry !== null &&
    (!needsOther || otherIndustry.trim().length > 0);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid || !role || !teamSize || !industry) return;
    onNext({
      organizationName: trimmedOrgName,
      role,
      teamSize,
      industry,
      ...(needsOther ? { otherIndustry: otherIndustry.trim() } : {}),
    });
  };

  return (
    <div>
      <h2 className="text-2xl md:text-3xl font-semibold text-fg mb-3 text-center">
        {t('saas.quickContext.title')}
      </h2>
      <p className="text-fg-muted mb-10 text-center max-w-lg mx-auto">
        {t('saas.quickContext.subtitle')}
      </p>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div>
          <label
            htmlFor="organizationName"
            className="block text-sm font-medium text-fg mb-3"
          >
            {t('saas.quickContext.orgNameLegend')}
          </label>
          <Input
            id="organizationName"
            type="text"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            placeholder={t('saas.quickContext.orgNamePlaceholder')}
            maxLength={ORG_NAME_MAX}
            autoComplete="organization"
            autoFocus
          />
        </div>

        <RadioCardGroup
          legend={t('saas.quickContext.roleLegend')}
          name="role"
          options={ROLES.map((value) => ({
            value,
            label: t(`saas.quickContext.role.${value}`),
          }))}
          value={role}
          onChange={(value) => setRole(value as OnboardingRole)}
          columns={{ sm: 2, md: 5 }}
        />

        <RadioCardGroup
          legend={t('saas.quickContext.teamSizeLegend')}
          name="teamSize"
          options={TEAM_SIZES.map((value) => ({
            value,
            label: t(`saas.quickContext.teamSize.${value}`),
          }))}
          value={teamSize}
          onChange={(value) => setTeamSize(value as TeamSize)}
          columns={{ sm: 3, md: 3 }}
        />

        <RadioCardGroup
          legend={t('saas.quickContext.industryLegend')}
          name="industry"
          options={INDUSTRIES.map((value) => ({
            value,
            label: t(`saas.quickContext.industry.${value}`),
          }))}
          value={industry}
          onChange={(value) => {
            setIndustry(value as Industry);
            if (value !== 'other') setOtherIndustry('');
          }}
          columns={{ sm: 2, md: 4 }}
        />

        {needsOther && (
          <div>
            <label
              htmlFor="otherIndustry"
              className="block text-sm font-medium text-fg mb-2"
            >
              {t('saas.quickContext.otherIndustryLabel')}
            </label>
            <Input
              id="otherIndustry"
              type="text"
              value={otherIndustry}
              onChange={(e) => setOtherIndustry(e.target.value)}
              placeholder={t('saas.quickContext.otherIndustryPlaceholder')}
              maxLength={100}
              autoFocus
            />
          </div>
        )}

        {error && (
          <p className="text-sm text-danger text-center" role="alert">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={!isValid || isSubmitting}
          size="lg"
          // Hero call-to-action: 48px, taller than the lg step (40px) — pre-existing, no variant for it yet.
          className="w-full min-h-12 gap-2"
        >
          {isSubmitting
            ? t('saas.quickContext.submitting')
            : t('saas.quickContext.continue')}
          {!isSubmitting && <ArrowRight className="h-5 w-5" />}
        </Button>
      </form>
    </div>
  );
}

interface RadioCardGroupProps {
  legend: string;
  name: string;
  options: Array<{ value: string; label: string }>;
  value: string | null;
  onChange: (value: string) => void;
  columns: { sm: number; md: number };
}

function RadioCardGroup({
  legend,
  name,
  options,
  value,
  onChange,
  columns,
}: RadioCardGroupProps) {
  const gridClass =
    GRID_CLASS[`${columns.sm}-${columns.md}`] ?? 'grid-cols-2 md:grid-cols-3';

  return (
    <fieldset>
      <legend className="block text-sm font-medium text-fg mb-3">{legend}</legend>
      <RadioGroup
        value={value ?? undefined}
        onValueChange={onChange}
        name={name}
        className={`grid gap-3 ${gridClass}`}
      >
        {options.map((opt) => (
          <RadioGroupPrimitive.Item
            key={opt.value}
            value={opt.value}
            className={`
              relative flex items-center justify-center rounded-card
              border border-border bg-bg-surface px-4 py-3 text-center text-sm
              font-medium text-fg-muted transition-colors duration-200
              hover:bg-accent hover:text-fg
              focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-ring focus-visible:ring-offset-2
              focus-visible:ring-offset-bg
              data-[state=checked]:border-primary
              data-[state=checked]:bg-primary-dim
              data-[state=checked]:text-fg
            `}
          >
            <span>{opt.label}</span>
          </RadioGroupPrimitive.Item>
        ))}
      </RadioGroup>
    </fieldset>
  );
}

const GRID_CLASS: Record<string, string> = {
  '2-3': 'grid-cols-2 md:grid-cols-3',
  '2-4': 'grid-cols-2 md:grid-cols-4',
  '2-5': 'grid-cols-2 md:grid-cols-5',
  '3-3': 'grid-cols-3',
};
